import { DropdownMenu as KobalteDropdownMenu } from "@kobalte/core/dropdown-menu"
import { extensions } from "@repo/fika-blog/src/extensions"
import { renderToText } from "@repo/fika-blog/src/lib/render-to-text"
import { MARKS, NODES } from "@repo/fika-blog/src/nodes"
import { SNIPPET_NODE } from "@repo/fika-blog/src/nodes/snippet"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import { parseLocale } from "@repo/fika-shared/src/locale"
import { translate } from "@repo/fika-shared/src/translate"
import type { UploadFolder } from "@repo/fika-shared/src/upload-folder.js"
import { type Accessor, createMemo, For, Show } from "solid-js"
import { createEditorTransaction } from "solid-tiptap"
import {
  dropdownContentClass,
  dropdownItemLabelClass,
  dropdownItemRecipe,
} from "@/designSystem/components/dropdown-menu.css"
import { Icon } from "@/designSystem/components/icon"
import { Mini } from "@/designSystem/components/mini/mini"
import { Tooltip } from "@/designSystem/components/tooltip"
import { useI18nContext } from "@/i18n/i18n-solid"
import { formatShortcut } from "@/lib/platform"
import { insertImageIntoEditor } from "@/pages/posts/insert-image-into-editor"
import { HighlightSelect } from "./highlight-select"
import {
  topToolbarClass,
  topToolbarTriggerClass,
  topToolbarTriggerIconClass,
} from "./top-toolbar.css"
import { useFormatToolbar } from "./use-format-toolbar"

interface TopToolbarProps {
  editor: Accessor<Editor | undefined>
  uploadFolder: Accessor<UploadFolder>
  subscribeBlockEnabled?: boolean
  subscribeFormId?: Accessor<string | null>
  subscribeForms?: Accessor<
    Array<{ id: string; name: string; content: string | null }>
  >
}

export function TopToolbar(props: TopToolbarProps) {
  const { LL, locale } = useI18nContext()
  let fileInputRef: HTMLInputElement | undefined

  const {
    linkValue,
    markValues,
    markOptions,
    markOnChange,
    linkOnChange,
    toggleList,
  } = useFormatToolbar({ editor: props.editor })

  const formatValues = () =>
    markOptions().filter(option =>
      ["bold", "italic", "strike", "code"].includes(option.value),
    )
  const subscribeForms = createMemo(() =>
    (props.subscribeForms?.() ?? []).map(form => ({
      id: form.id,
      label: translate(form.name, parseLocale(locale())) ?? form.name,
      content: form.content,
    })),
  )

  async function onImageChange(event: Event) {
    const editor = props.editor()
    if (!editor) return

    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    await insertImageIntoEditor({
      editor,
      file,
      uploadFolder: props.uploadFolder(),
    })

    input.value = ""
  }

  function toggleBlockquote() {
    const editor = props.editor()
    if (!editor) return
    editor.chain().focus().toggleBlockquote().run()
  }

  function addCallout() {
    const editor = props.editor()
    if (!editor) return
    editor.chain().focus().setCallout().run()
  }

  function openImagePicker() {
    fileInputRef?.click()
  }

  function addCodeBlock() {
    const editor = props.editor()
    if (!editor) return
    editor.chain().focus().setCodeBlock().run()
  }

  function addSnippet() {
    const editor = props.editor()
    if (!editor) return
    editor.chain().focus().insertContent({ type: SNIPPET_NODE }).run()
  }

  function addMathBlock() {
    const editor = props.editor()
    if (!editor) return
    editor.chain().focus().insertMathBlock().run()
  }

  function addEmbed() {
    const editor = props.editor()
    if (!editor) return
    editor.chain().focus().insertEmbed().run()
  }

  function addSubscribe() {
    const editor = props.editor()
    if (!editor) return
    const selectedFormId = props.subscribeFormId?.() ?? null
    const selectedForm = subscribeForms().find(
      form => form.id === selectedFormId,
    )
    const title = resolveSubscribeTitle(selectedForm?.content ?? null, locale())
    editor
      .chain()
      .focus()
      .insertSubscribe({ formId: selectedFormId, title })
      .run()
  }

  function addSubscribeForForm(formId: string | null) {
    const editor = props.editor()
    if (!editor) return
    const selectedForm = subscribeForms().find(form => form.id === formId)
    const title = resolveSubscribeTitle(selectedForm?.content ?? null, locale())
    editor.chain().focus().insertSubscribe({ formId, title }).run()
  }

  const canUndo = createEditorTransaction(
    props.editor,
    editor => editor?.can().undo() ?? false,
  )
  const canRedo = createEditorTransaction(
    props.editor,
    editor => editor?.can().redo() ?? false,
  )

  function undo() {
    const editor = props.editor()
    if (!editor) return
    editor.chain().focus().undo().run()
  }

  function redo() {
    const editor = props.editor()
    if (!editor) return
    editor.chain().focus().redo().run()
  }

  return (
    <Show when={props.editor()}>
      {editor => (
        <div class={topToolbarClass}>
          <Mini.Menu ghost>
            <Mini.Section>
              <Mini.ToggleGroupMultiple
                value={markValues()}
                onChange={markOnChange}
                values={formatValues()}
              />
              <Mini.ToggleGroup
                value={linkValue()}
                onChange={linkOnChange}
                values={[
                  {
                    value: "link",
                    iconName: MARKS.link.iconName,
                    title: LL().editor.link(),
                  },
                ]}
              />
              <HighlightSelect editor={editor()} />
              <Mini.Divider />
              <Tooltip
                description={LL().editor.undo()}
                shortcut={formatShortcut("Z")}
              >
                <Mini.Button
                  iconName="undo-2"
                  onClick={undo}
                  disabled={!canUndo()}
                />
              </Tooltip>
              <Tooltip
                description={LL().editor.redo()}
                shortcut={formatShortcut("Shift+Z")}
              >
                <Mini.Button
                  iconName="redo-2"
                  onClick={redo}
                  disabled={!canRedo()}
                />
              </Tooltip>
              <Mini.Divider />
              <Tooltip description={LL().editor.quote()}>
                <Mini.Button iconName="quote" onClick={toggleBlockquote} />
              </Tooltip>
              <Tooltip description="Callout">
                <Mini.Button iconName="messages-square" onClick={addCallout} />
              </Tooltip>
              <Tooltip description={LL().editor.image()}>
                <Mini.Button iconName="image" onClick={openImagePicker} />
              </Tooltip>
              <Tooltip description={LL().editor.bulletList()}>
                <Mini.Button
                  iconName="list"
                  onClick={() => toggleList("bulletList")}
                />
              </Tooltip>
              <Tooltip description={LL().editor.more()}>
                <KobalteDropdownMenu>
                  <KobalteDropdownMenu.Trigger
                    class={topToolbarTriggerClass}
                    aria-label={LL().editor.insertBlock()}
                  >
                    <span class={topToolbarTriggerIconClass} aria-hidden="true">
                      +
                    </span>
                  </KobalteDropdownMenu.Trigger>
                  <KobalteDropdownMenu.Portal>
                    <KobalteDropdownMenu.Content class={dropdownContentClass}>
                      <KobalteDropdownMenu.Item
                        class={dropdownItemRecipe({ hue: "sand" })}
                        onSelect={() => toggleList("orderedList")}
                      >
                        <Icon name="list-ordered" size="18" />
                        <span class={dropdownItemLabelClass}>
                          {LL().editor.orderedList()}
                        </span>
                      </KobalteDropdownMenu.Item>
                      <KobalteDropdownMenu.Item
                        class={dropdownItemRecipe({ hue: "sand" })}
                        onSelect={addCodeBlock}
                      >
                        <Icon name={NODES.codeBlock.iconName} size="18" />
                        <span class={dropdownItemLabelClass}>
                          {LL().editor.codeBlock()}
                        </span>
                      </KobalteDropdownMenu.Item>
                      <KobalteDropdownMenu.Item
                        class={dropdownItemRecipe({ hue: "sand" })}
                        onSelect={addSnippet}
                      >
                        <Icon name="puzzle" size="18" />
                        <span class={dropdownItemLabelClass}>
                          {LL().editor.snippet()}
                        </span>
                      </KobalteDropdownMenu.Item>
                      <KobalteDropdownMenu.Item
                        class={dropdownItemRecipe({ hue: "sand" })}
                        onSelect={addMathBlock}
                      >
                        <Icon name={NODES.math.iconName} size="18" />
                        <span class={dropdownItemLabelClass}>
                          {LL().editor.math()}
                        </span>
                      </KobalteDropdownMenu.Item>
                      <KobalteDropdownMenu.Item
                        class={dropdownItemRecipe({ hue: "sand" })}
                        onSelect={addEmbed}
                      >
                        <Icon name={NODES.embed.iconName} size="18" />
                        <span class={dropdownItemLabelClass}>
                          {LL().editor.embed()}
                        </span>
                      </KobalteDropdownMenu.Item>
                      <Show when={props.subscribeBlockEnabled !== false}>
                        <KobalteDropdownMenu.Item
                          class={dropdownItemRecipe({ hue: "sand" })}
                          onSelect={addSubscribe}
                        >
                          <Icon name="mail" size="18" />
                          <span class={dropdownItemLabelClass}>
                            {LL().editor.subscribe()}
                          </span>
                        </KobalteDropdownMenu.Item>
                        <Show when={subscribeForms().length > 0}>
                          <KobalteDropdownMenu.Sub>
                            <KobalteDropdownMenu.SubTrigger
                              class={dropdownItemRecipe({ hue: "sand" })}
                            >
                              <Icon name="gift" size="18" />
                              <span class={dropdownItemLabelClass}>
                                Add form
                              </span>
                            </KobalteDropdownMenu.SubTrigger>
                            <KobalteDropdownMenu.Portal>
                              <KobalteDropdownMenu.SubContent
                                class={dropdownContentClass}
                              >
                                <For each={subscribeForms()}>
                                  {form => (
                                    <KobalteDropdownMenu.Item
                                      class={dropdownItemRecipe({
                                        hue: "sand",
                                      })}
                                      onSelect={() =>
                                        addSubscribeForForm(form.id)
                                      }
                                    >
                                      <Icon name="mail" size="18" />
                                      <span class={dropdownItemLabelClass}>
                                        {form.label}
                                      </span>
                                    </KobalteDropdownMenu.Item>
                                  )}
                                </For>
                              </KobalteDropdownMenu.SubContent>
                            </KobalteDropdownMenu.Portal>
                          </KobalteDropdownMenu.Sub>
                        </Show>
                      </Show>
                    </KobalteDropdownMenu.Content>
                  </KobalteDropdownMenu.Portal>
                </KobalteDropdownMenu>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onImageChange}
                style={{ display: "none" }}
              />
            </Mini.Section>
          </Mini.Menu>
        </div>
      )}
    </Show>
  )
}

function resolveSubscribeTitle(content: string | null, locale: string) {
  if (!content?.trim()) return null

  const localized = translate(content, parseLocale(locale)) ?? content
  if (!localized.trim()) return null

  try {
    return renderToText({
      doc: JSON.parse(localized),
      extensions: extensions({ dropcursor: false }),
    }).trim()
  } catch {
    return localized.trim()
  }
}
